// The MOBILE reel timeline — a CapCut-style touch strip. The playhead is FIXED at the center
// and the strip scrolls under it, so native horizontal scrolling IS scrubbing (no drag-vs-scrub
// ambiguity to disambiguate). Beat cards are laid out on EFFECTIVE reel time (the same spans the
// master renders), the ACTIVE beat grows a right-edge trim handle (pointer-captured, so the
// browser never turns a trim into a scroll), a LONG-PRESS lifts a beat card into a reorder drag,
// a two-finger pinch scales the time axis, transition diamonds sit on the cuts, and a dashed
// "+" card appends a beat. Everything commits through useVideoTimeline() — the exact ops the
// desktop NLE uses — so undo/autosave/render behave identically.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Plus } from 'lucide-react'
import { cn } from 'lib/utils'
import type { VideoLayerClip } from '../types'
import { MIN_CLIP_SEC } from '../video-time'
import {
  useComposerEditor,
  useReelPlayhead,
  useVideoTimeline,
  type VideoTimelineContextValue,
} from './CompositionEditorProvider'
import { ClipFilmstrip, GeneratingVeil, layerLabel } from './ReelTimeline'
import { ROLE_LABEL } from './video-bg'
import { MIN_BEAT_SECONDS, MAX_BEAT_SECONDS } from './video-ops'

const DEFAULT_PX_PER_SEC = 48
const MIN_PX_PER_SEC = 24
const MAX_PX_PER_SEC = 96
const CARD_H = 60
const LANE_H = 20 // the free-clip lane above the beat row
const BEAT_TOP = LANE_H + 12 // beat row y (8px track padding + 4px gap under the lane)
const ADD_W = 52
/** After a programmatic smooth-scroll (beat tap), ignore scroll→seek this long. */
const SUPPRESS_MS = 550
/** Hold this long without moving to lift a beat card into reorder mode. */
const LONG_PRESS_MS = 500
/** Finger drift beyond this cancels the long-press (it's a scroll, not a hold). */
const LONG_PRESS_TOLERANCE = 8

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))

export function MobileReelTimeline() {
  const timeline = useVideoTimeline()
  const scrollerRef = useRef<HTMLDivElement>(null)
  const [containerW, setContainerW] = useState(0)
  const [pxPerSec, setPxPerSec] = useState(DEFAULT_PX_PER_SEC)
  const suppressUntil = useRef(0)
  const seekRaf = useRef<number | null>(null)
  // Live mirrors for the native (non-React) pinch listeners.
  const pxRef = useRef(pxPerSec)
  pxRef.current = pxPerSec
  // Live playhead: a per-tick SUBSCRIPTION (the shared context no longer carries a 60fps
  // number). This strip needs the tick anyway — it scroll-follows the playhead.
  const currentTime = useReelPlayhead(timeline)
  const timeRef = useRef(0)
  timeRef.current = currentTime

  useLayoutEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    const update = () => setContainerW(el.clientWidth)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Two-finger pinch scales the time axis (24–96 px/s), anchored on the playhead: the time
  // under the center line stays put by re-deriving scrollLeft from it at the new scale. Native
  // non-passive listeners because React's touch events can't preventDefault (the browser would
  // otherwise treat the second finger as more scrolling / page zoom).
  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    let pinch: { d0: number; px0: number } | null = null
    const dist = (t: TouchList) =>
      Math.hypot(t[0]!.clientX - t[1]!.clientX, t[0]!.clientY - t[1]!.clientY)
    const onStart = (e: TouchEvent) => {
      if (e.touches.length === 2) pinch = { d0: dist(e.touches), px0: pxRef.current }
    }
    const onMove = (e: TouchEvent) => {
      if (!pinch || e.touches.length !== 2) return
      e.preventDefault()
      const next = clamp((pinch.px0 * dist(e.touches)) / pinch.d0, MIN_PX_PER_SEC, MAX_PX_PER_SEC)
      setPxPerSec(next)
      suppressUntil.current = Date.now() + 200 // our own scrollLeft write must not re-seek
      el.scrollLeft = timeRef.current * next
    }
    const onEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) pinch = null
    }
    el.addEventListener('touchstart', onStart, { passive: true })
    el.addEventListener('touchmove', onMove, { passive: false })
    el.addEventListener('touchend', onEnd)
    el.addEventListener('touchcancel', onEnd)
    return () => {
      el.removeEventListener('touchstart', onStart)
      el.removeEventListener('touchmove', onMove)
      el.removeEventListener('touchend', onEnd)
      el.removeEventListener('touchcancel', onEnd)
    }
  }, [])

  // Follow the playhead while playing: the strip scrolls under the fixed center line. The
  // scroll handler ignores events while `playing`, so this write can never feed back into seek.
  const playing = timeline?.playing ?? false
  useEffect(() => {
    if (!playing) return
    const el = scrollerRef.current
    if (el) el.scrollLeft = currentTime * pxPerSec
  }, [playing, currentTime, pxPerSec])

  const onScroll = useCallback(() => {
    const tl = timeline
    const el = scrollerRef.current
    if (!tl || !el) return
    if (tl.playing) return // playback follow writes scrollLeft — never seek from those
    if (Date.now() < suppressUntil.current) return // a programmatic scroll settling
    if (seekRaf.current != null) return // rAF-throttle the seek to once per frame
    seekRaf.current = requestAnimationFrame(() => {
      seekRaf.current = null
      const t = clamp(el.scrollLeft / pxRef.current, 0, tl.totalDuration)
      tl.seek(t)
    })
  }, [timeline])
  useEffect(
    () => () => {
      if (seekRaf.current != null) cancelAnimationFrame(seekRaf.current)
    },
    []
  )

  if (!timeline) return null
  const px = pxPerSec
  const pad = Math.max(0, containerW / 2)
  const total = timeline.totalDuration
  const trackW = pad + total * px + 8 + ADD_W + pad

  const tapBeat = (i: number) => {
    const span = timeline.spans[i]
    if (!span) return
    const t = span.start + 0.01
    timeline.selectBeat(i)
    timeline.seek(t)
    suppressUntil.current = Date.now() + SUPPRESS_MS
    scrollerRef.current?.scrollTo({ left: t * px, behavior: 'smooth' })
  }

  return (
    <div className="relative flex-shrink-0 select-none">
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        onTouchStart={() => {
          if (timeline.playing) timeline.pause()
        }}
        className="overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        aria-label="Reel timeline, scroll to scrub"
      >
        <div className="relative py-2" style={{ width: trackW, height: CARD_H + LANE_H + 20 }}>
          {/* Free-clip lane: every visual layer as a draggable chip on absolute reel time. */}
          {timeline.clips.map(clip => (
            <ClipChip key={clip.layer.id} timeline={timeline} clip={clip} pad={pad} px={px} />
          ))}
          {timeline.beats.map((beat, i) => {
            const span = timeline.spans[i]
            if (!span) return null
            return (
              <BeatCard
                key={beat.id}
                timeline={timeline}
                index={i}
                left={pad + span.start * px}
                width={Math.max(8, (span.end - span.start) * px)}
                pad={pad}
                px={px}
                active={i === timeline.activeIndex}
                onTap={() => tapBeat(i)}
              />
            )
          })}
          {/* Transition diamonds ON the cuts (beat 1+): tap toggles cut ↔ crossfade. */}
          {timeline.beats.map((beat, i) => {
            if (i === 0) return null
            const span = timeline.spans[i]
            if (!span) return null
            const xfade = (beat.transitionIn ?? 'cut') === 'crossfade'
            return (
              <button
                key={`t-${beat.id}`}
                type="button"
                onClick={() => timeline.setBeatTransition(i, xfade ? 'cut' : 'crossfade')}
                aria-label={xfade ? 'Crossfade, tap for a hard cut' : 'Cut, tap for a crossfade'}
                title={xfade ? 'Crossfade (~0.4s)' : 'Hard cut'}
                className="absolute z-30 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center"
                style={{ left: pad + span.start * px, top: BEAT_TOP + CARD_H / 2 }}
              >
                <span
                  className={cn(
                    'block h-3 w-3 rotate-45 rounded-[2px] border transition-colors',
                    xfade ? 'border-acid bg-acid' : 'border-line-bright bg-bg-2'
                  )}
                />
              </button>
            )
          })}
          {/* Append a beat. */}
          {timeline.canAdd && (
            <button
              type="button"
              onClick={() => {
                const id = timeline.addBeat()
                if (!id) return
                // The new beat starts where the reel used to end — select + center it (the
                // spans in this closure predate the append, so compute from the old total).
                timeline.selectBeat(timeline.beats.length)
                timeline.seek(total + 0.01)
                suppressUntil.current = Date.now() + SUPPRESS_MS
                scrollerRef.current?.scrollTo({ left: total * px, behavior: 'smooth' })
              }}
              aria-label="Add beat"
              className="absolute flex flex-col items-center justify-center gap-0.5 rounded-[10px] border border-dashed border-line-bright text-paper-dim active:bg-bg-3"
              style={{ left: pad + total * px + 8, top: BEAT_TOP, width: ADD_W, height: CARD_H }}
            >
              <Plus size={16} />
              <span className="text-[9px] font-medium">Add</span>
            </button>
          )}
        </div>
      </div>
      {/* The fixed centered playhead. */}
      <div
        className="pointer-events-none absolute inset-y-0 left-1/2 z-40 w-[2px] -translate-x-1/2 rounded-full bg-acid"
        aria-hidden
      />
    </div>
  )
}

function BeatCard({
  timeline,
  index,
  left,
  width,
  pad,
  px,
  active,
  onTap,
}: {
  timeline: VideoTimelineContextValue
  index: number
  left: number
  width: number
  pad: number
  px: number
  active: boolean
  onTap: () => void
}) {
  const beat = timeline.beats[index]!
  const generating = timeline.generatingBeatIds.has(beat.id)
  const voicing = timeline.voicingBeatIds.has(beat.id)

  // LONG-PRESS reorder: hold ~500ms without drifting → the card "lifts" (scale + vibration),
  // then horizontal drag moves the beat live (moveBeat per crossing, ONE undo step via the
  // history gesture). Drifting before the hold fires cancels it — that's a scroll. While
  // lifted, a document-level non-passive touchmove preventDefault keeps the browser from
  // starting a scroll mid-drag.
  const [lifted, setLifted] = useState(false)
  const hold = useRef<{
    pointerId: number
    x: number
    y: number
    timer: ReturnType<typeof setTimeout>
  } | null>(null)
  const reorder = useRef<{ pointerId: number; from: number } | null>(null)
  const suppressClick = useRef(false)
  const timelineRef = useRef(timeline)
  timelineRef.current = timeline
  useEffect(
    () => () => {
      if (hold.current) clearTimeout(hold.current.timer)
      if (reorder.current) {
        reorder.current = null
        timelineRef.current.endHistoryGesture()
      }
    },
    []
  )
  useEffect(() => {
    if (!lifted) return
    const stop = (e: TouchEvent) => e.preventDefault()
    document.addEventListener('touchmove', stop, { passive: false })
    return () => document.removeEventListener('touchmove', stop)
  }, [lifted])

  const cancelHold = () => {
    if (hold.current) {
      clearTimeout(hold.current.timer)
      hold.current = null
    }
  }
  const endReorder = () => {
    cancelHold()
    if (!reorder.current) return
    reorder.current = null
    setLifted(false)
    suppressClick.current = true
    timeline.endHistoryGesture()
  }

  return (
    <div
      className="absolute"
      // The ACTIVE card sits ABOVE the transition diamonds (z-30): its trim handle overhangs
      // the right edge, and a diamond on that cut would otherwise swallow the trim pointerdown
      // (the card is a stacking context, so the handle's own z can never beat a sibling's).
      // The occluded diamond stays reachable by selecting the other beat or via the Beat sheet.
      style={{ left, width, top: BEAT_TOP, height: CARD_H, zIndex: lifted ? 40 : active ? 31 : 10 }}
    >
      <button
        type="button"
        onClick={() => {
          if (suppressClick.current) {
            suppressClick.current = false
            return
          }
          onTap()
        }}
        onPointerDown={e => {
          if (!e.isPrimary) return
          const el = e.currentTarget as HTMLElement
          const pointerId = e.pointerId
          const beatId = beat.id
          cancelHold()
          hold.current = {
            pointerId,
            x: e.clientX,
            y: e.clientY,
            timer: setTimeout(() => {
              hold.current = null
              try {
                el.setPointerCapture(pointerId)
              } catch {
                return // pointer already gone — no reorder
              }
              // Resolve the index by ID at fire time: beats can shift under the 500ms hold
              // (hydration tick, AI adopt) and the render-time `index` would then be stale.
              const from = timelineRef.current.beats.findIndex(b => b.id === beatId)
              if (from < 0) return
              reorder.current = { pointerId, from }
              setLifted(true)
              navigator.vibrate?.(10)
              timelineRef.current.beginHistoryGesture()
            }, LONG_PRESS_MS),
          }
        }}
        onPointerMove={e => {
          const h = hold.current
          if (h && e.pointerId === h.pointerId) {
            if (
              Math.abs(e.clientX - h.x) > LONG_PRESS_TOLERANCE ||
              Math.abs(e.clientY - h.y) > LONG_PRESS_TOLERANCE
            )
              cancelHold() // the finger is scrolling, not holding
            return
          }
          const r = reorder.current
          if (!r || e.pointerId !== r.pointerId) return
          const track = (e.currentTarget as HTMLElement).closest(
            '[aria-label^="Reel timeline"]'
          )?.firstElementChild
          if (!track) return
          const t = (e.clientX - track.getBoundingClientRect().left - pad) / px
          const tl = timelineRef.current
          // The beat span under the finger is the drop slot (self-stabilizing: after the
          // move the dragged beat owns that span, so the finger stays inside it).
          let target = tl.spans.findIndex(s => t < s.end)
          if (target === -1) target = tl.beats.length - 1
          target = clamp(target, 0, tl.beats.length - 1)
          if (target !== r.from) {
            tl.moveBeat(r.from, target)
            r.from = target
          }
        }}
        onPointerUp={endReorder}
        onPointerCancel={endReorder}
        aria-label={`Beat ${index + 1} (${ROLE_LABEL[beat.role]}), long-press to reorder`}
        aria-current={active}
        className={cn(
          'relative block h-full w-full overflow-hidden rounded-[10px] border text-left transition-all',
          lifted
            ? 'scale-105 border-acid shadow-2xl ring-2 ring-acid/60'
            : active
              ? 'border-acid ring-2 ring-acid/40'
              : 'border-line'
        )}
        style={lifted ? { touchAction: 'none' } : undefined}
      >
        <ClipFilmstrip beat={beat} />
        <span className="absolute top-1 left-1 z-10 rounded bg-black/60 px-1 text-[8px] leading-tight font-semibold tracking-wide text-paper uppercase">
          {ROLE_LABEL[beat.role]}
        </span>
        <span className="absolute right-1 bottom-1 z-10 rounded bg-black/60 px-1 text-[9px] leading-tight font-medium text-paper tabular-nums">
          {beat.durationSec.toFixed(1)}s
        </span>
        {generating && <GeneratingVeil label="Filming…" />}
        {!generating && voicing && <GeneratingVeil label="Recording…" />}
      </button>
      {active && !lifted && <TrimHandle timeline={timeline} index={index} px={px} />}
    </div>
  )
}

/** One free layer clip as a draggable chip on the lane above the beat row. Drag the body to
 *  slide it in time, drag either edge (12px zones) to stretch it, tap to select the layer on
 *  the canvas (which flips the toolbar to layer mode → Timing sheet for precision). Pointer
 *  capture + touch-action:none keeps a chip drag from ever becoming a strip scroll; the whole
 *  drag is ONE undo step via the history-gesture bracket. */
function ClipChip({
  timeline,
  clip,
  pad,
  px,
}: {
  timeline: VideoTimelineContextValue
  clip: VideoLayerClip
  pad: number
  px: number
}) {
  const ctx = useComposerEditor()
  const drag = useRef<{
    pointerId: number
    mode: 'move' | 'start' | 'end'
    startX: number
    origStart: number
    origEnd: number
    moved: boolean
  } | null>(null)
  const timelineRef = useRef(timeline)
  timelineRef.current = timeline
  useEffect(
    () => () => {
      if (drag.current) {
        drag.current = null
        timelineRef.current.endHistoryGesture()
      }
    },
    []
  )
  const layerId = clip.layer.id
  const selected = ctx?.editor.selectedId === layerId
  const left = pad + clip.start * px
  const width = Math.max(14, (clip.end - clip.start) * px)

  const select = () => {
    if (!ctx) return
    // The layer is only editable through the ACTIVE beat's view — jump the selection to a
    // beat this clip is on before selecting, so the canvas + toolbar actually pick it up.
    const idx = timeline.spans.findIndex(
      s => clip.start < s.end - 1e-6 && clip.end > s.start + 1e-6
    )
    if (idx >= 0 && idx !== timeline.activeIndex) timeline.selectBeat(idx)
    ctx.editor.select(layerId)
  }
  const end = () => {
    const d = drag.current
    if (!d) return
    drag.current = null
    timeline.endHistoryGesture()
    if (!d.moved) select() // a clean tap (no drag) selects
  }
  return (
    <div
      role="button"
      aria-label={`${layerLabel(clip.layer)} · on screen ${clip.start.toFixed(1)}–${clip.end.toFixed(1)}s`}
      onPointerDown={e => {
        e.stopPropagation()
        const el = e.currentTarget as HTMLElement
        try {
          el.setPointerCapture(e.pointerId)
        } catch {
          return // capture failed (pointer already gone) — no drag, no dangling gesture
        }
        const rect = el.getBoundingClientRect()
        const x = e.clientX - rect.left
        const mode =
          x < 12 && width > 34 ? 'start' : x > rect.width - 12 && width > 34 ? 'end' : 'move'
        drag.current = {
          pointerId: e.pointerId,
          mode,
          startX: e.clientX,
          origStart: clip.start,
          origEnd: clip.end,
          moved: false,
        }
        timeline.beginHistoryGesture()
      }}
      onPointerMove={e => {
        const d = drag.current
        if (!d || e.pointerId !== d.pointerId) return
        const dt = (e.clientX - d.startX) / px
        if (!d.moved && Math.abs(e.clientX - d.startX) < 4) return
        d.moved = true
        if (d.mode === 'move') timeline.moveClip(layerId, d.origStart + dt)
        else if (d.mode === 'start')
          timeline.resizeClip(
            layerId,
            'start',
            Math.min(d.origStart + dt, d.origEnd - MIN_CLIP_SEC)
          )
        else
          timeline.resizeClip(layerId, 'end', Math.max(d.origEnd + dt, d.origStart + MIN_CLIP_SEC))
      }}
      onPointerUp={end}
      onPointerCancel={end}
      className={cn(
        'absolute top-2 z-20 flex items-center overflow-hidden rounded-[6px] border px-1.5',
        selected
          ? 'border-acid bg-acid/25 text-paper'
          : 'border-line-bright bg-bg-3 text-paper-dim active:bg-bg-2'
      )}
      style={{ left, width, height: LANE_H, touchAction: 'none' }}
    >
      <span className="truncate text-[9px] leading-none font-medium">{layerLabel(clip.layer)}</span>
    </div>
  )
}

/** The active beat's right-edge trim handle. Pointer capture + touch-action:none means a drag
 *  here can never start a native scroll; the whole drag is ONE undo step via the history
 *  gesture bracket (pointercancel included — iOS fires it on system gestures). */
function TrimHandle({
  timeline,
  index,
  px,
}: {
  timeline: VideoTimelineContextValue
  index: number
  px: number
}) {
  const drag = useRef<{ pointerId: number; startX: number; startDur: number } | null>(null)
  // Unmount mid-drag (beat deleted / selection moved) must still close the history gesture.
  const timelineRef = useRef(timeline)
  timelineRef.current = timeline
  useEffect(
    () => () => {
      if (drag.current) {
        drag.current = null
        timelineRef.current.endHistoryGesture()
      }
    },
    []
  )
  const beat = timeline.beats[index]
  if (!beat) return null
  const end = () => {
    if (!drag.current) return
    drag.current = null
    timeline.endHistoryGesture()
  }
  return (
    <div
      role="slider"
      aria-label={`Beat ${index + 1} duration`}
      aria-valuenow={beat.durationSec}
      aria-valuemin={MIN_BEAT_SECONDS}
      aria-valuemax={MAX_BEAT_SECONDS}
      onPointerDown={e => {
        e.stopPropagation()
        try {
          ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
        } catch {
          return // capture failed (pointer already gone) — no drag, no dangling gesture
        }
        drag.current = { pointerId: e.pointerId, startX: e.clientX, startDur: beat.durationSec }
        timeline.beginHistoryGesture()
      }}
      onPointerMove={e => {
        const d = drag.current
        if (!d || e.pointerId !== d.pointerId) return
        const next = clamp(
          d.startDur + (e.clientX - d.startX) / px,
          MIN_BEAT_SECONDS,
          MAX_BEAT_SECONDS
        )
        timeline.trimBeat(index, next)
      }}
      onPointerUp={end}
      onPointerCancel={end}
      className="absolute top-1/2 -right-3 z-30 flex h-full w-8 -translate-y-1/2 items-center justify-center"
      style={{ touchAction: 'none' }}
    >
      <span className="block h-[70%] w-[5px] rounded-full bg-acid shadow-[0_0_0_1px_rgba(0,0,0,0.5)]" />
    </div>
  )
}
